import React, { useMemo, useState } from 'react';
import { Input } from '@storybook/input';
import './sso-configure.scss';
import { Button } from '@storybook';
import { useRecoilState } from 'recoil';
import { AuthSSOConfigurationState } from 'views/auth/hooks.ts';

const platformSections = [
	{
		key: 'web',
		icon: <i className="ri-global-line web-icon" style={{ fontSize: 32 }} />,
		label: 'Web',
		fields: [
			{
				label: 'Client ID',
				name: 'webClientId',
				inputType: 'text',
				placeholder: '',
			},
			{
				label: 'Client Secret',
				name: 'webClientSecret',
				inputType: 'text',
				placeholder: '',
			},
			//for apple
			{
				label: 'Key ID',
				name: 'keyId',
				inputType: 'text',
				placeholder: '',
			},
			//for apple
			{
				label: 'Team ID',
				name: 'teamId',
				inputType: 'text',
				placeholder: '',
			},
			{
				label: 'RedirectURL',
				name: 'webRedirectUrl',
				inputType: 'text',
				placeholder: '',
			},
		],
	},
	{
		key: 'ios',
		icon: <i className="ri-apple-fill ios-icon" style={{ fontSize: 32 }} />,
		label: 'iOS',
		fields: [
			{
				label: 'Client ID',
				name: 'iosClientId',
				inputType: 'text',
				placeholder: '',
			},
			{
				label: 'RedirectURL',
				name: 'iosRedirectUrl',
				inputType: 'text',
				placeholder: '',
			},
		],
	},
	{
		key: 'android',
		icon: (
			<i className="ri-android-fill android-icon" style={{ fontSize: 32 }} />
		),
		label: 'Android',
		fields: [
			{
				label: 'Client ID',
				name: 'androidClientId',
				inputType: 'text',
				placeholder: '',
			},
			{
				label: 'RedirectURL',
				name: 'androidRedirectUrl',
				inputType: 'text',
				placeholder: '',
			},
		],
	},
];

export const SSOConfigure = ({
	name,
	closeModal,
}: {
	name: string;
	closeModal: VoidFunction;
}) => {
	type FormState = {
		webClientId: string;
		webClientSecret: string;
		keyId: string;
		teamId: string;
		webRedirectUrl: string;
		iosClientId: string;
		androidClientId: string;
		[key: string]: string;
	};
	const [form, setForm] = useState<FormState>({
		webClientId: '',
		webClientSecret: '',
		keyId: '',
		teamId: '',
		webRedirectUrl: '',
		iosClientId: '',
		androidClientId: '',
	});
	const [errors, setErrors] = useState<{ [key: string]: string }>({});
	const [ssoConfiguration, setSSOConfiguration] = useRecoilState(
		AuthSSOConfigurationState
	);

	// Get visible fields for validation
	const fields = useMemo(() => {
		return platformSections
			.filter(section => {
				if (name === 'google') return true;
				return section.key === 'web';
			})
			.map(section => {
				const newFields = section.fields.filter(field => {
					if (name === 'google' || name !== 'apple') {
						return field.name !== 'keyId' && field.name !== 'teamId';
					}
					return true; // keep all for Apple
				});
				return { ...section, fields: newFields };
			});
	}, [name]);

	// Flat list of all visible field names
	const visibleFieldNames = useMemo(() => {
		return fields.flatMap(section => section.fields.map(f => f.name));
	}, [fields]);

	// Validate all visible fields
	const validate = (values: typeof form) => {
		const newErrors: { [key: string]: string } = {};
		visibleFieldNames.forEach(field => {
			const value = values[field];
			if (!value || value.toString().trim() === '') {
				newErrors[field] = 'This field is required';
			} else if (field === 'webRedirectUrl') {
				// Validate URL for webRedirectUrl
				try {
					// Only validate if not empty
					new URL(value);
				} catch {
					newErrors[field] = 'Please enter a valid URL';
				}
			}
		});
		return newErrors;
	};

	// On input change, update value and validate
	const handleChange =
		(fieldName: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			setForm(prev => {
				const updated = { ...prev, [fieldName]: value };
				return updated;
			});
		};

	// const isFormValid = Object.keys(errors).length === 0 && visibleFieldNames.every(f => form[f] && form[f]?.toString().trim() !== '');

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		const validation = validate(form);
		setErrors(validation);
		if (Object.keys(validation).length > 0) return;
		const payload: Record<string, any> = {};
		if (name === 'google') {
			payload[name] = {
				web: {
					clientId: form.webClientId,
					clientSecret: form.webClientSecret,
					redirectURI: form.webRedirectUrl,
				},
				ios: {
					clientId: form.iosClientId,
					redirectURI: form.iosRedirectUrl,
				},
				android: {
					clientId: form.androidClientId,
					redirectURI: form.androidRedirectUrl,
				},
			};
		} else {
			payload[name] = {
				web: {
					clientId: form.webClientId,
					clientSecret: form.webClientSecret,
					redirectURI: form.webRedirectUrl,
					...(name === 'apple' && {
						keyId: form.keyId,
						teamId: form.teamId,
					}),
				},
			};
		}
		setSSOConfiguration({ ...ssoConfiguration, ...payload });
		closeModal?.();
	};

	return (
		<form className="sso-configure-card" onSubmit={handleSubmit}>
			<div className="sso-configure-card-fields">
				{fields.map(section => (
					<div key={section.key} className="sso-platform-section">
						<div className="sso-platform-header">
							<div className="sso-platform-icon">{section.icon}</div>
							<div className="sso-platform-labels">
								<div className="sso-platform-label">Platform</div>
								<div className="sso-platform-title">{section.label}</div>
							</div>
							<div className="divider-sso"></div>
						</div>
						<div className="sso-input-grid">
							{section.fields.map(field => (
								<div
									key={field.name}
									className={
										section.key === 'web' && field.label === 'RedirectURL'
											? 'sso-input-full'
											: ''
									}
								>
									<Input
										label={field.label}
										inputType={field.inputType as any}
										placeholder={field.placeholder}
										handleChange={handleChange(field.name)}
										value={form[field.name as keyof typeof form]}
										id={field.name}
										isError={!!errors[field.name]}
										errorMessage={errors[field.name]}
									/>
								</div>
							))}
						</div>
					</div>
				))}
			</div>
			<div className="sso-submit-footer">
				<Button
					label="Close"
					type={'button button__filled--danger view-kyc-modal--danger-button '}
					// eslint-disable-next-line no-console
					handleClick={() => console.log(false)}
				/>
				<Button
					label={'Submit'}
					handleClick={handleSubmit}
					type={
						'button button__filled button__filled--primary view-kyc-modal--proceed-btn '
					}
					// disabled={ !isFormValid}
				/>
			</div>
		</form>
	);
};

export default SSOConfigure;
